// ============================================
// DisputesPage — hàng chờ khiếu nại hoá đơn (sheet 2 mục #58/#60)
// ============================================
//
// "Tôi không lấy món này" là khiếu nại thường gặp nhất của mô hình lấy-rồi-đi:
// khách đã bị trừ tiền theo số hệ thống nhìn thấy, đây là đường sửa duy nhất.
//
// Hai điều khác biệt so với các hàng chờ khác, đừng làm mất khi refactor:
// 1. Chốt từng DÒNG, không phải cả phiếu — một khiếu nại có thể duyệt món này
//    và từ chối món kia (BE trả PARTIALLY_APPROVED).
// 2. Máy không bao giờ tự từ chối. Mọi ca REJECTED đều do người bấm ở đây, nên
//    khi có dòng bị từ chối thì `note` là BẮT BUỘC — nó là thứ duy nhất khách
//    đọc được để hiểu vì sao (BE trả lại dưới tên `resolution_note`).

import { useMemo, useState } from 'react';
import type { Dispute, DisputeItem } from '@conai/shared-types';
import { useI18n } from '../../i18n';
import {
  PageContainer,
  PageHeader,
  SelectDropdown,
  DataTable,
  Pagination,
  type Column,
} from '../../components/shared';
import { useDisputes, useResolveDispute } from '../../hooks/useDisputes';
import { useActiveCurrency } from '../../hooks/useActiveCurrency';
import { useStoreSelectionStore } from '../../stores';
import { extractList } from '../../utils/apiList';
import { formatMoney } from '../../utils/currency';

const PAGE_SIZE = 20;

const STATUS_STYLES: Record<string, string> = {
  PENDING_REVIEW: 'bg-amber-50 text-amber-700',
  APPROVED: 'bg-emerald-50 text-emerald-700',
  PARTIALLY_APPROVED: 'bg-blue-50 text-blue-700',
  REJECTED: 'bg-red-50 text-red-700',
};

function formatDateTime(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function DisputesPage() {
  const { t } = useI18n();
  const selectedStoreId = useStoreSelectionStore((s) => s.selectedStoreId);

  const [statusFilter, setStatusFilter] = useState<string>('PENDING_REVIEW');
  const [currentPage, setCurrentPage] = useState(1);
  const [selected, setSelected] = useState<Dispute | null>(null);

  const { data, isLoading, error } = useDisputes({
    page: currentPage,
    page_size: PAGE_SIZE,
    store_id: selectedStoreId || undefined,
    status: statusFilter === 'all' ? undefined : statusFilter,
  });

  const rows = useMemo(() => extractList<Dispute>(data), [data]);
  const totalItems =
    (data as { count?: number } | undefined)?.count ?? rows.length;
  const totalPages = Math.max(Math.ceil(totalItems / PAGE_SIZE), 1);

  const columns: Column<Dispute>[] = [
    {
      key: 'created_at',
      header: t('date'),
      width: '150px',
      render: (row) => (
        <p className="text-xs font-medium text-slate-600">{formatDateTime(row.created_at)}</p>
      ),
    },
    {
      key: 'dispute_id',
      header: t('disputeIdColumn'),
      width: '130px',
      render: (row) => (
        <p className="truncate font-mono text-xs text-slate-500" title={row.dispute_id}>
          {row.dispute_id.slice(0, 8)}
        </p>
      ),
    },
    {
      key: 'items',
      header: t('disputeItemsColumn'),
      render: (row) => (
        <div>
          <p className="text-sm font-semibold text-slate-800">
            {t('disputeItemsCount', { count: row.items?.length ?? 0 })}
          </p>
          <p className="truncate text-xs text-slate-500">
            {(row.items ?? []).map((i) => t(`disputeReason${i.reason}`)).join(', ')}
          </p>
        </div>
      ),
    },
    {
      key: 'status',
      header: t('status'),
      width: '150px',
      render: (row) => (
        <div className="space-y-1">
          <span
            className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-bold ${
              STATUS_STYLES[row.status] ?? 'bg-slate-100 text-slate-700'
            }`}
          >
            {t(`disputeStatus${row.status}`)}
          </span>
          {/* Máy tự duyệt thì không có người nào bấm — đánh dấu để nhân viên
              không đi tìm ai đã xử lý. */}
          {row.auto_resolved ? (
            <p className="text-[10px] text-slate-400">{t('disputeAutoResolved')}</p>
          ) : null}
        </div>
      ),
    },
    {
      key: 'action',
      header: '',
      width: '110px',
      render: (row) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setSelected(row);
          }}
          className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50"
        >
          {row.status === 'PENDING_REVIEW' ? t('disputeReview') : t('viewDetails')}
        </button>
      ),
    },
  ];

  return (
    <PageContainer>
      <PageHeader title={t('disputesTitle')} subtitle={t('disputesSubtitle')} />

      {error && (
        <div className="mt-4 rounded-lg bg-red-50 p-4 text-sm text-red-700">
          {error instanceof Error ? error.message : t('disputesLoadError')}
        </div>
      )}

      <div className="mt-6 rounded-xl border border-slate-200 bg-white px-4 py-3">
        <SelectDropdown
          options={[
            { value: 'PENDING_REVIEW', label: t('disputeStatusPENDING_REVIEW') },
            { value: 'APPROVED', label: t('disputeStatusAPPROVED') },
            { value: 'PARTIALLY_APPROVED', label: t('disputeStatusPARTIALLY_APPROVED') },
            { value: 'REJECTED', label: t('disputeStatusREJECTED') },
            { value: 'all', label: t('allStatus') },
          ]}
          value={statusFilter}
          onChange={(val) => {
            setStatusFilter(val);
            setCurrentPage(1);
          }}
          placeholder={t('allStatus')}
          className="w-full sm:w-[220px]"
        />
      </div>

      <div className="mt-4 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <DataTable
          columns={columns}
          data={rows}
          rowKey="dispute_id"
          isLoading={isLoading}
          onRowClick={(row) => setSelected(row)}
          showFilter={false}
          showHeaderMenu={false}
          emptyMessage={t('disputesEmpty')}
          className="border-0 shadow-none"
        />
        {!isLoading && totalItems > 0 && (
          <div className="border-t border-slate-100">
            <Pagination
              variant="numbered"
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              pageSize={PAGE_SIZE}
              onPageChange={setCurrentPage}
              itemLabel="disputes"
            />
          </div>
        )}
      </div>

      {selected && (
        <DisputeResolveModal dispute={selected} onClose={() => setSelected(null)} />
      )}
    </PageContainer>
  );
}

// ============================================
// Modal chốt khiếu nại
// ============================================

function DisputeResolveModal({
  dispute,
  onClose,
}: {
  dispute: Dispute;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const { mutate: resolveDispute, isPending } = useResolveDispute();
  // Tiền tệ lấy từ CHÍNH khiếu nại — snapshot `session.store.currency` lúc tạo
  // (BE xác nhận 03/09: `SessionDispute.currency` có từ đầu). Không dùng tiền
  // tệ của cửa hàng đang chọn: hàng đợi gồm nhiều cửa hàng khác thị trường.
  // Thiếu field (bản QA cũ) thì rơi về cửa hàng đang chọn.
  const activeCurrency = useActiveCurrency();
  const currency = dispute.currency || activeCurrency;

  const isPendingReview = dispute.status === 'PENDING_REVIEW';

  /** dispute_item_id -> duyệt hay không. Mặc định duyệt hết (ca thường gặp). */
  const [decisions, setDecisions] = useState<Record<string, boolean>>(() =>
    Object.fromEntries((dispute.items ?? []).map((i) => [i.dispute_item_id, true]))
  );
  const [note, setNote] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const hasRejection = Object.values(decisions).some((approved) => !approved);

  const handleSubmit = () => {
    setFormError(null);
    // Note bắt buộc khi từ chối — xem ghi chú đầu file.
    if (hasRejection && !note.trim()) {
      setFormError(t('disputeNoteRequiredOnReject'));
      return;
    }
    resolveDispute(
      {
        disputeId: dispute.dispute_id,
        data: {
          items: (dispute.items ?? []).map((item) => ({
            dispute_item_id: item.dispute_item_id,
            approved: decisions[item.dispute_item_id] ?? false,
          })),
          note: note.trim() || undefined,
        },
      },
      {
        onSuccess: onClose,
        onError: (err: unknown) => {
          setFormError(err instanceof Error ? err.message : t('disputeResolveFailed'));
        },
      }
    );
  };

  const renderItemDecision = (item: DisputeItem) => {
    const approved = decisions[item.dispute_item_id] ?? false;
    return (
      <div key={item.dispute_item_id} className="rounded-xl border border-slate-200 p-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-slate-800">
              {t(`disputeReason${item.reason}`)}
            </p>
            <p className="text-xs text-slate-500">
              {t('disputeQuantityClaimed', { qty: item.quantity_claimed })}
              {' · '}
              {t('disputeOrderItem', { id: String(item.order_item_id) })}
            </p>
            {item.note ? (
              <p className="mt-1 text-xs italic text-slate-600">“{item.note}”</p>
            ) : null}
            {item.refund_amount ? (
              <p className="mt-1 text-xs font-semibold text-emerald-700">
                {/* BE trả tiền dạng chuỗi (DecimalString); formatMoney của
                    store-admin nhận number — ép ở biên, không đổi chữ ký hàm
                    dùng chung. */}
                {t('disputeRefunded')}: {formatMoney(Number(item.refund_amount), { currency })}
              </p>
            ) : null}
          </div>

          {isPendingReview ? (
            <div className="flex shrink-0 overflow-hidden rounded-lg border border-slate-200">
              <button
                type="button"
                onClick={() =>
                  setDecisions((prev) => ({ ...prev, [item.dispute_item_id]: true }))
                }
                className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                  approved ? 'bg-emerald-50 text-emerald-700' : 'text-slate-500 hover:bg-slate-50'
                }`}
              >
                {t('disputeApprove')}
              </button>
              <button
                type="button"
                onClick={() =>
                  setDecisions((prev) => ({ ...prev, [item.dispute_item_id]: false }))
                }
                className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                  !approved ? 'bg-red-50 text-red-700' : 'text-slate-500 hover:bg-slate-50'
                }`}
              >
                {t('disputeReject')}
              </button>
            </div>
          ) : (
            <span
              className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold ${
                item.approved ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
              }`}
            >
              {item.approved ? t('disputeApproved') : t('disputeRejected')}
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-[560px] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div>
            <h3 className="text-lg font-bold text-slate-900">{t('disputeDetailTitle')}</h3>
            <p className="font-mono text-xs text-slate-500">{dispute.dispute_id}</p>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto p-6">
          {isPendingReview ? (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
              {t('disputeRefundWarning')}
            </p>
          ) : null}

          {(dispute.items ?? []).map(renderItemDecision)}

          {isPendingReview ? (
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
                {t('disputeNoteLabel')}
                {hasRejection ? <span className="ml-1 text-red-500">*</span> : null}
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                placeholder={t('disputeNotePlaceholder')}
                className="w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary-400"
              />
            </div>
          ) : dispute.resolution_note ? (
            <div className="rounded-lg bg-slate-50 px-3 py-2">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
                {t('disputeNoteLabel')}
              </p>
              <p className="mt-1 text-sm text-slate-700">{dispute.resolution_note}</p>
            </div>
          ) : null}

          {formError ? (
            <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{formError}</div>
          ) : null}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-100 px-6 py-4">
          <button
            onClick={onClose}
            disabled={isPending}
            className="rounded-lg bg-slate-100 px-5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-200 disabled:opacity-50"
          >
            {isPendingReview ? t('cancel') : t('close')}
          </button>
          {isPendingReview ? (
            <button
              onClick={handleSubmit}
              disabled={isPending}
              className="rounded-lg bg-primary-600 px-5 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {isPending ? t('saving') : t('disputeSubmitResolution')}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
